import $ from "jquery";
import ko from "knockout";
import componentCache from "flow/utils/componentCache";
import workingContext from "flow/viewModels/workingContext";
import Workflow from "flow/untested/Workflow";
import config from "flow/config";
import ajaxErrorHelper from "flow/ajax/ajaxErrorHelper";
import elementFactory from "flow/untested/elementFactory";
import manifestManager from "flow/utils/manifestManager";
import MyFlow from "flow/MyFlow";
import Component from "flow/Component";
import { Dictionary } from "./api/types";
import activator from "./utils/activator";
import * as homeConfiguration from "flow/stores/homeConfiguration";
import bind from "bind-decorator";

export type CanLeaveEntry = {
    component: Component;
    result: string;
};

export default class Viewport {
    $window = elementFactory.window();
    $canvas = elementFactory.canvas;
    isMobileResolution = ko.observable(false);

    canvasHeight = 0;
    canvasWidth = 0;
    windowWidth = 0;

    configuredDuration = 300;
    minWidth = 768;
    newWorkingContextOrderOffset = 20000;

    components = ko.observableArray<Component>([]);
    selectedComponent = ko.observable<Component>();
    selectedContainerId = ko.pureComputed<string>(() => this.selectedComponent()?.myFlow?.id);
    visibleComponents = ko.pureComputed<Component[]>(() => {
        const visibleArr = this.components().filter(c => c.isVisible());

        visibleArr.sort((c1, c2) => c1.order.compare(c2.order));

        return visibleArr;
    });

    showBottomNavigator = ko.pureComputed<boolean>(() => {
        const visibleArr = this.components().filter(c => c.isVisible() && !c.isShadow());
        return visibleArr.length > 1;
    });

    workflow = new Workflow();

    private readonly resizeObserver = new ResizeObserver(this.onCanvasResize);
    private ignoreResizeEvents = false;

    constructor(private readonly myFlow: MyFlow) {
        window.addEventListener("resize", () => {
            this.updateWindowWidth();

            if (this.windowWidth <= this.minWidth) {
                this.isMobileResolution(true);
            } else {
                this.isMobileResolution(false);
            }
        });

        // Move between containers using Ctrl+Arrow (left/right)
        // Maximize/Minimize using Ctrl+Arrow (up/down)
        window.addEventListener("keydown", (event: KeyboardEvent) => {
            if (event.ctrlKey === false || event.keyCode === 17) return;

            if (event.keyCode === 37) {
                this.previous();
            } else if (event.keyCode === 39) {
                this.next();
            } else if (event.keyCode === 38) {
                this.selectedComponent().isMaximized(true);

                this.layout(this.configuredDuration);
                this.reselect(this.configuredDuration);
            } else if (event.keyCode === 40) {
                this.selectedComponent().isMaximized(false);

                this.layout(this.configuredDuration);
                this.reselect(this.configuredDuration);
            }
        });
        //

        myFlow.subscribe(config.topics.workingContextChangeStarted, () => {
            const componentsToRemove: Component[] = [];

            // Ensure all containers activated by the new working context will end up before existing ones that still pass though the visibility filter.
            this.components().forEach(item => {
                if (item.manifest.reuseInstance === false) {
                    componentsToRemove.push(item);
                }

                item.order.old(true);

                if (item.isVisible()) {
                    item.onHide();
                }

                if (item.isSelected()) {
                    item.isSelected(false);
                    this.selectedComponent(undefined);
                }
            });

            componentsToRemove.forEach(c => {
                this.components.remove(c);
                c.onDestroy();
            });
        });

        myFlow.subscribe(config.topics.workingContextChanged, () => {
            const contextComponent = workingContext.currentComponent();
            if (contextComponent !== null && contextComponent !== undefined && workingContext.isWorkflow()) {
                this.workflow.activate(contextComponent.viewModel());
            } else {
                this.workflow.deactivate();
            }

            this.layout(0);

            if (this.selectedComponent() === undefined) {
                this.select(this.visibleComponents()[0], 0);
            }
        });

        // Toggle maximize/minimize
        myFlow.subscribe(config.topics.resizeComponent, component => {
            this.resizeComponent(component);
        });

        // Workaround for layout getting disturbed when container gets its html injected
        myFlow.subscribe("component.setupComponent", () => {
            this.layout(0);
            this.reselect(0);
        });

        // Fade out/in canvas on menu toggle to avoid continuously recalculating layout during animation
        myFlow.subscribe(config.topics.menuChanged, () => {
            this.ignoreResizeEvents = true;
            this.$canvas().fadeOut(100);

            setTimeout(() => {
                this.$canvas().fadeIn(200);

                setTimeout(() => {
                    this.layout(0);
                    this.reselect(0);
                    this.ignoreResizeEvents = false;
                }, 20);
            }, 300);
        });

        // React to homescreen order updates
        myFlow.subscribe(config.topics.homescreenUpdated, () => {
            this.visibleComponents().forEach(component => {
                component.order.overriddenBase(
                    homeConfiguration.getSortOrder(component.manifest.id, component.manifest.order)
                );
            });

            this.layout(0);

            if (!this.selectedContainerId() || this.selectedContainerId() === config.componentIds.shadowContainer) {
                this.selectFirst(true);
            } else {
                this.reselect(0);
            }
        });
        //

        this.visibleComponents
            .extend({ rateLimit: { timeout: 200, method: "notifyWhenChangesStop" } })
            .subscribe(() => {
                this.layout(0);
            });

        this.updateWindowWidth();

        if (this.visibleComponents().length > 0) {
            this.layout(0);
            this.select(this.visibleComponents()[0], 0);
        }

        this.isMobileResolution(this.windowWidth <= this.minWidth);

        this.resizeObserver.observe(this.$canvas()[0]);
    }

    @bind
    onCanvasResize(entries: ResizeObserverEntry[]) {
        const entry = entries[0];
        this.canvasWidth = this.isMobileResolution() ? this.windowWidth : entry.contentBoxSize[0].inlineSize;
        this.canvasHeight = entry.contentBoxSize[0].blockSize;

        if (!this.ignoreResizeEvents) {
            this.layout(0);
            this.reselect(0);
        }
    }

    updateWindowWidth() {
        this.windowWidth = this.$window.width();
    }

    private debounce = false;

    layout(duration: number) {
        if (this.debounce) {
            // There is already a layout queued up that will redraw what we want it to
            return;
        }

        this.debounce = true;
        window.requestAnimationFrame(() => {
            this.debounce = false;

            this.updateVisibilities();
            this.adjustWidthAndHeight();
            this.setComponentValues();
            this.renderComponentValues(duration);
        });
    }

    shouldBeVisible(component: Component) {
        return (
            component.isShadow() ||
            (workingContext.shouldBeVisible(component.manifest.id, component.hint()) && !component.order.old())
        );
    }

    updateVisibilities() {
        this.components().forEach(component => {
            component.isVisible(this.shouldBeVisible(component));
        });
    }

    adjustWidthAndHeight() {
        this.visibleComponents().forEach(component => {
            if (!component.element()) return;

            if (this.adjustWidth(component)) {
                component.minimizedWidth = this.canvasWidth;
                component.adjustedWidth(true);
            } else {
                component.minimizedWidth = component.manifest.minimizedWidth;
                component.adjustedWidth(false);
            }

            if (this.adjustHeight(component)) {
                component.minimizedHeight = this.canvasHeight;
                component.adjustedHeight(true);
            } else {
                component.minimizedHeight = component.manifest.minimizedHeight;
                component.adjustedHeight(false);
            }

            if (component.isShadow()) {
                component.minimizedHeight = this.canvasHeight;
                component.minimizedWidth = this.canvasWidth;
            }
        });
    }

    adjustWidth(component: Component) {
        if (this.windowWidth < this.minWidth) return true;

        if (component.manifest.minimizedWidth > this.canvasWidth) return true;

        if (component.manifest.minimizedWidth === undefined || component.manifest.minimizedWidth === null) return true;

        return false;
    }

    adjustHeight(component: Component) {
        if (this.windowWidth < this.minWidth) return true;

        if (component.manifest.minimizedHeight > this.canvasHeight) return true;

        if (component.manifest.minimizedHeight === undefined || component.manifest.minimizedHeight === null)
            return true;

        return false;
    }

    setComponentValues() {
        const processedItems: Component[] = [];

        this.visibleComponents().forEach((component, i) => {
            if (i === 0) {
                component.x = 0;
                component.y = 0;
            } else {
                const previousItem = this.visibleComponents()[i - 1];

                let x;
                let y;

                if (previousItem.y + this.getHeight(previousItem) + this.getHeight(component) <= this.canvasHeight) {
                    x = previousItem.x;
                    y =
                        previousItem.y +
                        this.getHeight(previousItem) -
                        (this.isMobileResolution() ? 0 : config.viewportMargin);
                } else {
                    const temp = processedItems.filter(value => value.x === previousItem.x);
                    const sortedTemp = temp.sort((a, b) => this.getWidth(b) - this.getWidth(a));

                    const maxItem = sortedTemp[0];

                    x =
                        maxItem.x +
                        this.getWidth(maxItem) -
                        (this.maximizeViewportToEdge(previousItem) ? 0 : config.viewportMargin);
                    if (!this.isMobileResolution() && this.maximizeViewportToEdge(component)) {
                        x += config.viewportMargin;
                    }
                    y = 0;
                }

                component.x = x;
                component.y = y;
            }

            processedItems.push(component);

            i++;
        });
    }

    renderComponentValues(duration: number) {
        this.visibleComponents().forEach(component => {
            if (!component.element()) return;

            const leftTopRightBottom = this.maximizeViewportToEdge(component) ? 0 : config.viewportMargin;

            const d1 = component
                .element()
                .children()
                .animate(
                    {
                        left: leftTopRightBottom,
                        top: leftTopRightBottom,
                        right: leftTopRightBottom,
                        bottom: leftTopRightBottom
                    },
                    duration
                )
                .promise()
                .then(() => {
                    if (this.maximizeViewportToEdge(component)) {
                        component.element().addClass("lc-edge-maximized-container");
                    } else {
                        component.element().removeClass("lc-edge-maximized-container");
                    }
                });

            const d2 = component
                .element()
                .animate(
                    {
                        left: component.x,
                        top: component.y,
                        width: this.getWidth(component),
                        height: this.getHeight(component)
                    },
                    duration
                )
                .promise();

            $.when(d1, d2).then(() => {
                component.onContainerSizeChanged();
            });
        });
    }

    maximizeViewportToEdge(component: Component) {
        return (
            this.isMobileResolution() ||
            (component &&
                (component.manifest.isVisibleOnHomeScreen || component.manifest.hasNoWorkingContext) &&
                component.manifest.maximizeToEdge &&
                (component.isMaximized() || (this.adjustWidth(component) && this.adjustHeight(component))))
        );
    }

    getHeight(component: Component) {
        if (component.isMaximized()) {
            return this.canvasHeight;
        } else {
            return component.minimizedHeight;
        }
    }

    getWidth(component: Component) {
        if (component.isMaximized()) {
            return this.canvasWidth;
        } else {
            return component.minimizedWidth;
        }
    }

    reselect(duration: number) {
        this.select(this.selectedComponent(), duration, true);
    }

    select(component: Component, duration: number, isReselection?: boolean) {
        if (this.selectedComponent() && this.selectedComponent() !== component) {
            this.selectedComponent().isSelected(false);
        }

        this.selectedComponent(component);

        if (component === undefined || component === null) {
            return;
        }

        component.isSelected(true);

        if (isReselection !== true) {
            component.load().done(() => {
                component.onSelected();
            });
        }

        window.requestAnimationFrame(() => {
            // Always animate, even if on the right place to avoid strange IE bug in live env :/
            //if (canvas().parent().scrollLeft() !== parseInt(component.x)) {
            this.$canvas().parent().animate({ scrollLeft: component.x }, duration);
            //}

            component.load().done(() => {
                component.onFocus();
            });
        });
    }

    resizeComponent(component: Component) {
        component.isMaximized(!component.isMaximized());

        this.layout(this.configuredDuration);
        this.select(component, this.configuredDuration);
    }

    canLeave(id: string) {
        const canLeaveResponses: CanLeaveEntry[] = [];
        this.visibleComponents().forEach(component => {
            const result = component.canLeave(id);
            if (result) {
                canLeaveResponses.push({ component: component, result: result });
            }
        });
        return canLeaveResponses;
    }

    defragComponentOrderValues() {
        // Defragmentation of sort order values
        const compArray = this.components.slice(0);
        compArray.sort((c1, c2) => c1.order.compare(c2.order));
        for (let i = 0; i < compArray.length; ++i) {
            compArray[i].order.activationNumber(i + 1);
        }
    }

    performSortOrderHousekeepingAndUpdateLayout() {
        this.defragComponentOrderValues();

        // Force visibleComponents to recalculate since we've updated indices after the last update to the list.
        this.components.valueHasMutated();

        this.layout(0);
    }

    addComponentLast(manifest: any, viewContext: Dictionary) {
        const component = componentCache.getComponentByManifest(manifest);
        const shadowComponent = componentCache.getShadowComponent();

        // Put component last.
        const currentMax = this.components().reduce((max, c) => Math.max(max, c.order.activationNumber()), 0);
        component.order.activationNumber(currentMax + 1);
        component.order.old(false);

        if (this.components().indexOf(component) === -1) {
            this.components.push(component);
        }
        if (shadowComponent != undefined && this.components().indexOf(shadowComponent) === -1) {
            this.components.push(shadowComponent);
        }

        component.isVisible(this.shouldBeVisible(component));

        this.performSortOrderHousekeepingAndUpdateLayout();

        return this.loadComponent(component, viewContext);
    }

    addComponentBesideExisting(existingComponent: Component, manifest: any, viewContext: Dictionary) {
        const component = componentCache.getComponentByManifest(manifest);

        const compList = this.components();
        for (let i = 0; i < compList.length; ++i) {
            // Increase order by one for all components that should end up after
            if (compList[i].order.activationNumber() > existingComponent.order.activationNumber()) {
                compList[i].order.activationNumber(compList[i].order.activationNumber() + 1);
            }
        }

        // Put our component in the hole we created in the list
        component.order.activationNumber(existingComponent.order.activationNumber() + 1);
        component.order.old(false);

        if (this.components().indexOf(component) === -1) {
            this.components.push(component);
        }

        this.performSortOrderHousekeepingAndUpdateLayout();

        return this.loadComponent(component, viewContext);
    }

    loadComponent(component: Component, viewContext: Dictionary) {
        const dfd = $.Deferred();

        if (component?.manifest?.isVisibleOnHomeScreen) {
            const overriddenOrder = homeConfiguration.getSortOrder(component.manifest.id, component.manifest.order);
            component.order.overriddenBase(overriddenOrder);
        }

        component
            .load()
            .done(() => {
                component.activate(viewContext);
                component.onShow();
                if (this.isMobileResolution()) {
                    this.myFlow.publish(config.topics.componentAddedMobileMode, component.manifest.id);
                }

                dfd.resolve();
            })
            .fail(possibleJqXhr => {
                this.components.remove(component);
                componentCache.remove(component);

                if (
                    ajaxErrorHelper.isjqXhrObject(possibleJqXhr) &&
                    ajaxErrorHelper.isConnectionProblem(possibleJqXhr)
                ) {
                    component.myFlow.handleAjaxError(possibleJqXhr);
                } else {
                    this.myFlow.notifyError("Failed to load component with id: " + component.manifest.id);
                }

                dfd.reject();
            });

        return dfd.promise();
    }

    removeComponent(component: Component) {
        const selectedComponentCandidate = this.getSelectedComponentCandidate(component);

        this.components.remove(component);
        component.onHide();

        if (component.manifest.reuseInstance === false) {
            component.onDestroy();
        }

        this.layout(0);

        if (selectedComponentCandidate !== null) {
            this.select(selectedComponentCandidate, 0);
        }
    }

    resetWorkingContextComponents() {
        const componentsToRemove: Component[] = [];

        this.components().forEach(item => {
            if (item.manifest.reuseInstance === false) {
                componentsToRemove.push(item);
            } else {
                if (item.isVisible()) {
                    item.onHide();
                    item.isVisible(false);
                }
            }

            item.order.old(true);

            if (item.isSelected()) {
                item.isSelected(false);
                this.selectedComponent(undefined);
            }
        });

        componentsToRemove.forEach(c => {
            this.components.remove(c);
            c.onDestroy();
        });
    }

    getSelectedComponentCandidate(component: Component) {
        let selectedComponentCandidate = null;

        if (this.selectedComponent() === component) {
            const index = this.visibleComponents().indexOf(component);
            if (this.visibleComponents().length > 1) {
                if (index === 0) {
                    selectedComponentCandidate = this.visibleComponents()[1];
                } else {
                    selectedComponentCandidate = this.visibleComponents()[index - 1];
                }
            } else {
                selectedComponentCandidate = undefined;
            }
        }

        return selectedComponentCandidate;
    }

    previous() {
        if (this.visibleComponents().indexOf(this.selectedComponent()) === 0) return;

        this.select(
            this.visibleComponents()[this.visibleComponents().indexOf(this.selectedComponent()) - 1],
            this.configuredDuration
        );
    }

    next() {
        const filteredVisibleComponents = this.visibleComponents().filter(component => !component.isShadow());
        if (filteredVisibleComponents.indexOf(this.selectedComponent()) === filteredVisibleComponents.length - 1)
            return;

        this.select(
            this.visibleComponents()[this.visibleComponents().indexOf(this.selectedComponent()) + 1],
            this.configuredDuration
        );
    }

    selectById(id: string, filter: (hint?: any) => boolean, skipAnimation?: boolean) {
        const componentList = this.visibleComponents().filter(component => {
            return component.manifest.id === id && (filter ? filter(component.hint()) : true);
        });

        if (componentList.length > 0) {
            if (filter) {
                // Don't bother, it is most likely a container that won't be present on a reload
                activator.updateSelectedContainerInUrl(undefined);
            } else {
                activator.updateSelectedContainerInUrl(componentList[0]?.manifest?.id);
            }

            this.select(componentList[0], skipAnimation ? 0 : this.configuredDuration);
            return true;
        }

        return false;
    }

    selectByInstance(component: Component) {
        if (this.visibleComponents().indexOf(component) !== -1) {
            if (component?.hint()) {
                // Don't bother, it is most likely a container that won't be present on a reload
                activator.updateSelectedContainerInUrl(undefined);
            } else {
                activator.updateSelectedContainerInUrl(component?.manifest?.id);
            }

            this.select(component, this.configuredDuration);
            return true;
        }

        return false;
    }

    isComponentVisible(id: string, filter: (hint?: any) => boolean) {
        const manifest = manifestManager.getManifestWithId(id);
        if (!manifest) {
            return false;
        }

        if (manifest.isWorkingContext) {
            return workingContext.currentId() === id;
        }

        const componentList = this.visibleComponents().filter(component => {
            return component.manifest.id === id && (filter ? filter(component.hint()) : true);
        });

        return componentList.length > 0;
    }

    selectFirst(skipAnimation: boolean) {
        const firstContainer = this.visibleComponents().find(
            component => !component.isShadow() && !component.order.old()
        );
        if (firstContainer) {
            activator.updateSelectedContainerInUrl(firstContainer.manifest?.id);
            this.select(firstContainer, skipAnimation ? this.configuredDuration : 0);
        }
    }
}
